// The chat's times: each message's clock time (messageClockTime), the chat
// list stamp (messageTimeLabel) and the divider above each day.
//
// Each message shows only its clock time ("18:53", "6:53 PM" in the US), since
// the divider above it carries the date (owner decision 7 Oct 2026). The chat
// list keeps the relative stamp below.
//
// Mirror of the Flutter app: the stamp is TimeAgo.format
// (lib/features/messaging/utils/time_ago.dart), the divider is _DayDivider in
// message_thread_view.dart. Change them together.
//
//   under a minute   Now
//   under an hour    5m
//   under a day      3h
//   under a week     2d       (whole days elapsed, not calendar days)
//   older            25 Sep   (follows the device locale, so Sep 25 in the US; the app always writes Sep 25)
//   over 12 months   25 Aug 2025 (the year only then; owner decision 7 Oct 2026)
//
// "Over 12 months" (isOverAYearOld): earlier than local midnight on the same
// calendar day one year before now. On 29 Feb that day does not exist and the
// line falls on 1 Mar (Date rolls over; Dart's DateTime does the same).
//
// Reported 7 Oct 2026: the website showed a message older than a week as its
// clock time alone ("18:53"), so nobody could tell which day it was from.
//
// Pure, no Nuxt imports: callers pass "now" and the locale, so the tests read
// the clock once and the component reads the device locale.

import {useDates} from '~/composables/useDates';

const {formatDate, formatDayMonth, formatTime, formatWeekdayDate, formatWeekdayDayMonth} = useDates();

const BARE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * The instant a message was sent. A date-time with no Z or offset is read as
 * UTC, which is how the API itself reads one (DateTimeJsonConverter); left to
 * `new Date`, it would be read as the browser's local time and shift by the
 * reader's offset. The API always writes the Z today, so this is a guard.
 */
export const messageInstant = (value) => {
    if (value === null || value === undefined || value === '') return null;
    let date;
    if (value instanceof Date) {
        date = value;
    } else if (typeof value === 'string' && BARE_DATE_TIME.test(value)) {
        date = new Date(`${value}Z`);
    } else {
        date = new Date(value);
    }
    return Number.isNaN(date.getTime()) ? null : date;
};

/** The clock time a message was sent, in the reader's locale. Empty when unusable. */
export const messageClockTime = (createdAt, locale) => {
    const date = messageInstant(createdAt);
    if (date === null) return '';
    return formatTime(date, locale) ?? '';
};

/**
 * Whether `date` is more than 12 months before `now`: earlier than the start of
 * the same local calendar day one year back. Below that line a date carries no
 * year (the reader assumes this year); from it, the year is shown.
 */
export const isOverAYearOld = (date, now) =>
    date.getTime() < new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime();

/** "Now", "5m", "3h", "2d", else the date ("25 Sep", or "25 Aug 2025" over a year). Empty when unusable. */
export const messageTimeLabel = (createdAt, now, locale) => {
    const date = messageInstant(createdAt);
    if (date === null) return '';
    // A stamp in the future (a fast clock somewhere) reads as Now, as the app.
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return 'Now';
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    if (isOverAYearOld(date, now)) return formatDate(date, locale) ?? '';
    return formatDayMonth(date, locale) ?? '';
};

const localDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** "Today", "Yesterday", else "Wed 30 Sep" ("Mon 25 Aug 2025" over a year). By the reader's calendar day. */
export const messageDayLabel = (createdAt, now, locale) => {
    const date = messageInstant(createdAt);
    if (date === null) return '';
    const diff = Math.round((localDay(now) - localDay(date)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (isOverAYearOld(date, now)) return formatWeekdayDate(date, locale) ?? '';
    return formatWeekdayDayMonth(date, locale) ?? '';
};

/** Whether `message` is the first of its local day, after `previous`. */
export const startsNewDay = (previous, message) => {
    const current = messageInstant(message?.createdAt);
    if (current === null) return false;
    const before = messageInstant(previous?.createdAt);
    if (before === null) return true;
    return localDay(before).getTime() !== localDay(current).getTime();
};
